import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { CloudLightning, Rocket, Zap, ArrowRight, Menu, X, Users } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent
} from './ui/navigation-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to section if hash is present after navigation
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.substring(1);
      const section = document.getElementById(sectionId);
      if (section) {
        setTimeout(() => {
          section.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  // Helper for anchor navigation
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (href.startsWith('/#')) {
      const sectionId = href.split('#')[1];
      if (location.pathname === '/') {
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        } else {
          // fallback: update hash to trigger useEffect
          window.location.hash = `#${sectionId}`;
        }
      } else {
        // Navigate to home with state to trigger scrolling after navigation
        navigate('/', { state: { scrollToSection: sectionId } });
      }
    } else {
      navigate(href);
    }

    // Close mobile menu if open
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  // Content for the mobile menu
  const renderMobileMenu = () => {
    return (
      <div className="absolute right-0 top-12 w-64 p-4 bg-slate-900/95 backdrop-blur-xl border border-slate-800/50 shadow-xl rounded-lg flex flex-col gap-4 z-50">
        {/* Platform Section */}
        <div className="border-b border-slate-700 pb-3">
          <div className="font-medium text-gray-100 mb-2">Platform</div>
          <div className="flex flex-col gap-2 ml-2">
            <Link
              to="/features"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 text-sm text-gray-300 hover:text-white py-1"
            >
              <Rocket className="h-4 w-4 text-blue-400" />
              <span>Features</span>
            </Link>

            <Link
              to="/use-cases"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 text-sm text-gray-300 hover:text-white py-1"
            >
              <Users className="h-4 w-4 text-green-400" />
              <span>Use Cases</span>
            </Link>

            <Link
              to="/compare"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 text-sm text-gray-300 hover:text-white py-1"
            >
              <Zap className="h-4 w-4 text-yellow-400" />
              <span>Why SkyTest</span>
            </Link>
          </div>
        </div>

        {/* Other Nav Items */}
        <div className="flex flex-col gap-2">
          <Link
            to="/blog"
            className="text-sm text-gray-300 hover:text-white py-1"
            onClick={() => setMobileMenuOpen(false)}
          >
            Blog
          </Link>
        </div>

        {/* Mobile Get Early Access Button */}
        <a
          href="/#waitlist"
          className="mt-2 px-4 py-2 rounded-full text-white text-sm font-medium transition-all duration-300 bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 flex items-center justify-center no-underline hover:text-white"
          onClick={(e) => handleNavClick(e, "/#waitlist")}
        >
          Get Early Access
          <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </a>
      </div>
    );
  };

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-4 px-1.5 md:px-6 lg:px-12',
        isScrolled
          ? 'bg-dark-bg/80 backdrop-blur-lg shadow-lg'
          : 'bg-transparent'
      )}
    >
      <div className="container mx-auto">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="text-2xl font-bold tracking-tight text-white flex items-center"
            aria-label="Home"
          >
            <CloudLightning className="mr-2 w-8 h-8" />
            SkyTest.ai
          </Link>

          {/* Desktop Navigation */}
          {!isMobile && (
            <div className="flex items-center">
              <NavigationMenu>
                <NavigationMenuList className="flex items-center space-x-8">
                  {/* Platform Dropdown */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-sm text-gray-300 !text-gray-300 px-0 focus:outline-none bg-transparent border-none shadow-none !bg-transparent !border-none !shadow-none hover:!bg-transparent focus:!bg-transparent data-[state=open]:!bg-transparent data-[state=open]:!border-none data-[state=open]:!shadow-none hover:!text-gray-300 focus:!text-gray-300 data-[state=open]:!text-gray-300">
                      Platform
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-80 p-4 bg-slate-900/95 backdrop-blur-xl border border-slate-800/50 shadow-xl rounded-lg flex flex-col gap-3">
                        <Link
                          to="/features"
                          className="group flex flex-col p-3 rounded hover:bg-slate-800/60 transition-colors"
                        >
                          <div className="flex items-center gap-2 text-gray-200 group-hover:text-white">
                            <Rocket className="h-5 w-5 text-blue-400" />
                            <span className="font-medium">Features</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1 ml-7">
                            Explore SkyTest's AI-powered test automation capabilities.
                          </p>
                        </Link>

                        <Link
                          to="/use-cases"
                          className="group flex flex-col p-3 rounded hover:bg-slate-800/60 transition-colors"
                        >
                          <div className="flex items-center gap-2 text-gray-200 group-hover:text-white">
                            <Users className="h-5 w-5 text-green-400" />
                            <span className="font-medium">Use Cases</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1 ml-7">
                            See how teams test real multi-user business workflows.
                          </p>
                        </Link>

                        <Link
                          to="/compare"
                          className="group flex flex-col p-3 rounded hover:bg-slate-800/60 transition-colors"
                        >
                          <div className="flex items-center gap-2 text-gray-200 group-hover:text-white">
                            <Zap className="h-5 w-5 text-yellow-400" />
                            <span className="font-medium">Why SkyTest</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1 ml-7">
                            Compare SkyTest with testRigor, QA Wolf, Octomind, and mabl.
                          </p>
                        </Link>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  {/* Blog */}
                  <NavigationMenuItem>
                    <Link
                      to="/blog"
                      className="text-sm text-gray-300 hover:text-white transition-default focus-ring px-0"
                    >
                      Blog
                    </Link>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>

              {/* Desktop Get Early Access Button */}
              <a
                href="/#waitlist"
                className="ml-8 px-3 py-1.5 rounded-full text-white text-sm font-medium transition-all duration-300 bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 hover:shadow-md hover:transform hover:scale-105 flex items-center no-underline hover:text-white"
                onClick={(e) => handleNavClick(e, "/#waitlist")}
              >
                Get Early Access
                <ArrowRight className="ml-1 h-3.5 w-3.5 transition-all duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          )}

          {/* Mobile Navigation */}
          {isMobile && (
            <div className="relative">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-white rounded-md hover:bg-slate-800/40"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              {/* Mobile Menu Content */}
              {mobileMenuOpen && renderMobileMenu()}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
